import { useLocalization } from '@magicdoor/ui';
import { useNavigate } from '@solidjs/router';
import { createMemo, createSignal } from 'solid-js';
import { toast } from '~/components/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { AnnouncementRepository } from '~/repositories/announcementRepository';
import { createTriggerResource } from '~/utils/resource';
import { isEmailContentEmpty } from '~/utils/validations';
import type { Model } from '~/pages/announcements/add-announcement/types';
import type { AnnouncementListQuery } from '~/repositories/announcementRepository';
const repo = new AnnouncementRepository();

const addIfNotEmpty = <T extends keyof Model>(key: T, value: Model[T] | undefined, target: FormData | Record<string, any>) => {
  if (value !== undefined && value !== null && value !== '') {
    const targetKey = key;
    if (Array.isArray(value)) {
      if (value.length > 0) {
        if (target instanceof FormData) {
          value.forEach((item) => target.append(targetKey, item.toString()));
        } else {
          target[targetKey] = value;
        }
      }
    } else if (typeof value === 'boolean') {
      if (target instanceof FormData) {
        target.append(targetKey, value.toString());
      } else {
        target[targetKey] = value;
      }
    } else if (value instanceof File) {
      if (target instanceof FormData) {
        target.append(targetKey, value);
      }
    } else {
      if (target instanceof FormData) {
        target.append(targetKey, value.toString());
      } else {
        target[targetKey] = value;
      }
    }
  }
};

export const [AnnouncementProvider, useAnnouncements] = createMagicDoorContext('Announcements', () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [announcements, setAnnouncements] = createSignal<MagicDoor.Api.AnnouncementsDto[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<Error | null>(null);
  const [announcementReview, setAnnouncementReview] = createSignal<any>();

  const steps = createMemo(() => [t('Select target'), t('Content'), t('Review')]);

  const [announcement, getAnnouncement] = createTriggerResource(repo.getAnnouncementDetails.bind(repo));

  const fetchAnnouncementTargets = async (filter?: MagicDoor.Api.GetAnnouncementTargetsRequestDto) => {
    setIsLoading(true);
    try {
      const response = await repo.getAnnouncementTargets(filter);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      toast(t('Failed to fetch announcement targets'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnnouncements = async (filter?: AnnouncementListQuery) => {
    setIsLoading(true);
    try {
      const response = await repo.getAnnouncements(filter);
      setAnnouncements(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      toast(t('Failed to fetch announcements'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const createAnnouncement = async (model: Model) => {
    const emailMessage = model.emailMessage && !isEmailContentEmpty(model.emailMessage) ? model.emailMessage : undefined;

    const response = await repo.createAnnouncementWithJSON({
      targetType: model.targetType,
      hasBalanceDue: model.hasBalanceDue,
      includeEndedLease: model.includeEndedLease,
      portfolioIds: model.portfolioIds?.length ? model.portfolioIds : null,
      propertyIds: model.propertyIds?.length ? model.propertyIds : null,
      unitIds: model.unitIds?.length ? model.unitIds : null,
      chatMessage: model.chatMessage,
      emailMessage,
      chatAttachmentFiles: model.chatPreUploadedAttachments?.map((attachment) => attachment.fileId),
      emailAttachmentFiles: model.emailPreUploadedAttachments?.map((attachment) => attachment.fileId),
      subject: model.subject,
    });

    toast.success(t('Announcement has been sent successfully'));
    navigate(`/communications/announcements/${response?.id}`, { replace: true });
  };

  const getAnnouncementTargets = async (currentModel: Model) => {
    const filter: Partial<MagicDoor.Api.GetAnnouncementTargetsRequestDto> = {};

    addIfNotEmpty('targetType', currentModel.targetType as MagicDoor.Api.TargetType, filter);
    addIfNotEmpty('hasBalanceDue', currentModel.hasBalanceDue, filter);
    addIfNotEmpty('includeEndedLease', currentModel.includeEndedLease, filter);
    addIfNotEmpty('portfolioIds', currentModel.portfolioIds, filter);
    addIfNotEmpty('propertyIds', currentModel.propertyIds, filter);
    addIfNotEmpty('unitIds', currentModel.unitIds, filter);

    if (Object.keys(filter).length > 0) {
      const response = await fetchAnnouncementTargets(filter as MagicDoor.Api.GetAnnouncementTargetsRequestDto);
      setAnnouncementReview(response);
    }
  };

  return {
    announcements,
    isLoading,
    error,
    fetchAnnouncementTargets,
    fetchAnnouncements,
    getAnnouncement,
    announcement,
    createAnnouncement,
    getAnnouncementTargets,
    announcementReview,
    steps,
  };
});
